import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Compass } from "lucide-react";

export default function AdminStrategyOperator() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/strategy-operator/plans");
      setPlans(data.plans || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (tier, next) => {
    setSaving(tier);
    try {
      await api.put(`/admin/strategy-operator/plans/${tier}`, { enabled: next });
      toast.success(`${tier} → Strategy Operator ${next ? "ON" : "OFF"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  return (
    <div data-testid="admin-strategy-operator">
      <div className="dms-label mb-3 inline-flex items-center gap-2"><Compass className="w-3 h-3" /> Premium feature</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Strategy Operator access</h1>
      <p className="mt-2 text-[#475569] text-[14px] max-w-2xl">
        Control which subscription plans unlock the 90-Day Strategy Operator by default.
        Individual workspaces can be overridden from any user&apos;s detail page.
      </p>

      <div className="mt-6 dms-card overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="text-left text-[11px] tracking-[0.2em] uppercase font-bold text-[#64748B] border-b border-[#E2E8F0]">
              <th className="p-3">Plan</th>
              <th className="p-3">Tier key</th>
              <th className="p-3 text-right">Strategy Operator</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
            ) : plans.map(p => (
              <tr key={p.tier} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                <td className="p-3 font-semibold text-[#0A2342]">{p.name}</td>
                <td className="p-3 text-[#94A3B8]">{p.tier}</td>
                <td className="p-3 text-right">
                  <button
                    disabled={saving === p.tier}
                    onClick={() => toggle(p.tier, !p.enabled)}
                    data-testid={`plan-toggle-${p.tier}`}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${p.enabled ? "bg-[#4FD1C5]" : "bg-[#CBD5E1]"} ${saving === p.tier ? "opacity-60" : ""}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${p.enabled ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 dms-card p-5 text-[13px] text-[#475569] max-w-2xl">
        <div className="font-heading font-bold text-[#0A2342] mb-2">How this works</div>
        <ol className="pl-5 list-decimal space-y-1.5">
          <li>Enable a plan → every workspace whose owner is on that plan gets Strategy Operator by default.</li>
          <li>Per-workspace overrides (set via the API or a user&apos;s detail page) always win — even a Free-tier workspace can be granted access, or a Pro workspace explicitly denied.</li>
          <li>The current default: Business Unlimited (`pro_unlimited`) is ON. All others OFF.</li>
        </ol>
      </div>
    </div>
  );
}
